import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

export type ProfileType = 'coach' | 'player' | 'sponsor' | 'fan';

export interface Profile {
  id: string;
  user_id: string;
  type: ProfileType;
  display_name: string;
  avatar_url?: string;
  created_at: string;
}

interface ProfileContextType {
  profiles: Profile[];
  activeProfile: Profile | null;
  loading: boolean;
  error: string | null;
  setActiveProfile: (profile: Profile) => void;
  createProfile: (type: ProfileType, displayName: string) => Promise<{ error: Error | null }>;
  refreshProfiles: () => Promise<void>;
}

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

export const ProfileProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [activeProfile, setActiveProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchedForUserRef = useRef<string | null>(null);

  // Save active profile to localStorage
  const setActiveProfileWithPersistence = (profile: Profile) => {
    setActiveProfile(profile);
    if (profile && user) {
      localStorage.setItem(`activeProfile_${user.id}`, profile.id);
    }
  };

  const fetchProfiles = async () => {
    console.log('[ProfileContext] fetchProfiles called, user:', user?.id, user?.email);

    if (!user) {
      console.log('[ProfileContext] No user, clearing profiles');
      setProfiles([]);
      setActiveProfile(null);
      setError(null);
      setLoading(false);
      return;
    }

    try {
      setError(null);
      console.log('[ProfileContext] Fetching profiles for user_id:', user.id);

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: true });

      console.log('[ProfileContext] Query result - data:', data, 'error:', error);

      if (error) {
        console.error('[ProfileContext] Supabase error fetching profiles:', error);
        const errorMsg = `Failed to load profiles: ${error.message} (Code: ${error.code})`;
        setError(errorMsg);
        setProfiles([]);
        setLoading(false);
        return;
      }

      const profilesArray = data || [];
      console.log('[ProfileContext] Found', profilesArray.length, 'profiles:', profilesArray);
      setProfiles(profilesArray);

      // Try to restore last active profile from localStorage
      if (profilesArray.length > 0) {
        const savedProfileId = localStorage.getItem(`activeProfile_${user.id}`);
        console.log('[ProfileContext] Saved profile ID from localStorage:', savedProfileId);

        if (savedProfileId) {
          const savedProfile = profilesArray.find(p => p.id === savedProfileId);
          if (savedProfile) {
            console.log('[ProfileContext] ✓ Restoring saved profile:', savedProfile.display_name);
            setActiveProfile(savedProfile);
            setLoading(false);
            return;
          } else {
            console.log('[ProfileContext] Saved profile not found in current profiles');
          }
        }

        // If no saved profile or not found, use first profile
        console.log('[ProfileContext] ✓ Using first profile:', profilesArray[0].display_name);
        setActiveProfile(profilesArray[0]);
        localStorage.setItem(`activeProfile_${user.id}`, profilesArray[0].id);
      } else {
        console.log('[ProfileContext] ⚠ No profiles found for this user');
        setActiveProfile(null);
      }
    } catch (error: any) {
      console.error('[ProfileContext] Exception fetching profiles:', error);
      setError(`Failed to load profiles: ${error?.message || 'Unknown error'}`);
      setProfiles([]);
      setActiveProfile(null);
    } finally {
      console.log('[ProfileContext] fetchProfiles complete, loading=false');
      fetchedForUserRef.current = user?.id ?? null;
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      setLoading(true);
      fetchProfiles();
    } else {
      fetchedForUserRef.current = null;
      setProfiles([]);
      setActiveProfile(null);
      setLoading(false);
    }
  }, [user?.id]); // Only re-fetch when user ID changes

  const createProfile = async (type: ProfileType, displayName: string) => {
    if (!user) {
      return { error: new Error('User not authenticated') };
    }

    try {
      const { data, error } = await supabase
        .from('profiles')
        .insert([
          {
            user_id: user.id,
            type,
            display_name: displayName,
          },
        ])
        .select()
        .single();

      if (error) throw error;

      console.log('[ProfileContext] Profile created:', data);

      // Set as active profile immediately
      setActiveProfile(data);
      if (user) {
        localStorage.setItem(`activeProfile_${user.id}`, data.id);
      }

      // Refresh profiles list
      await fetchProfiles();

      return { error: null };
    } catch (error) {
      console.error('[ProfileContext] Error creating profile:', error);
      return { error: error as Error };
    }
  };

  const refreshProfiles = async () => {
    await fetchProfiles();
  };

  // If user exists but we haven't fetched profiles for this user yet,
  // treat as loading — this covers the gap between auth resolving and the
  // useEffect running (effects run AFTER render)
  const isLoading = loading || (!!user && fetchedForUserRef.current !== user.id);

  const value = {
    profiles,
    activeProfile,
    loading: isLoading,
    error,
    setActiveProfile: setActiveProfileWithPersistence,
    createProfile,
    refreshProfiles,
  };

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
};

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (context === undefined) {
    throw new Error('useProfile must be used within a ProfileContext');
  }
  return context;
};
